import Seo from "../components/Seo";
import LeadForm from "../components/LeadForm";
import FAQ from "../components/FAQ";
import ServiceCTA from "../components/service/ServiceCTA";
import { KineticLines, Reveal, SectionLabel } from "../components/Reveal";
import { Globe, PhoneCall, Cloud, MessagesSquare, AppWindow, Bot } from "lucide-react";
import { ArrowRight } from "lucide-react";

const AREAS = [
  { icon: Globe, title: "Internet & connectivity", copy: "Dedicated access, backup paths, and bandwidth that fits how you actually operate." },
  { icon: PhoneCall, title: "Business phone systems", copy: "Modern voice and unified communications — reviewed against what you pay today." },
  { icon: Cloud, title: "Cloud services", copy: "Hosting, storage, and infrastructure rightsized to your real usage." },
  { icon: MessagesSquare, title: "Collaboration technology", copy: "Meetings, messaging, and file-sharing tools evaluated for value, not just features." },
  { icon: AppWindow, title: "Software solutions", copy: "Licenses and subscriptions that may be duplicating work — or each other." },
  { icon: Bot, title: "AI & automation opportunities", copy: "Where emerging tools could save real hours without disrupting your team." },
];

const FAQS = [
  { q: "What does a technology review cover?", a: "Internet and connectivity, business phone systems, cloud services, collaboration tools, software, and where AI or automation could help — all reviewed through the lens of both cost and effectiveness." },
  { q: "Do I need new technology?", a: "Not necessarily. Sometimes the best outcome is confirming your current setup is right-sized. If a change makes sense, options are explained with costs before you decide." },
  { q: "Is the initial evaluation really no-cost?", a: "Where applicable, specialists may begin with a no-cost initial technology evaluation. Anything beyond that is explained clearly — including any costs — before you decide." },
  { q: "Will you push a specific product?", a: "No. We help connect you with independent specialists; any recommendation comes from the evaluation itself, and you stay in control of every decision." },
];

function TechNetworkViz() {
  const nodes = [
    { x: 90, y: 70, label: "Cloud" }, { x: 250, y: 50, label: "Phones" },
    { x: 400, y: 90, label: "Connectivity" }, { x: 150, y: 210, label: "Software" },
    { x: 350, y: 225, label: "Collaboration" }, { x: 250, y: 135, label: "AI & Automation" },
  ];
  const edges = [[0, 5], [1, 5], [2, 5], [3, 5], [4, 5], [0, 1], [1, 2], [3, 4]];
  return (
    <div className="relative rounded-3xl bg-navy-950 border border-white/10 shadow-core p-6 md:p-8 overflow-hidden" data-testid="technology-viz">
      <div className="absolute inset-0 bg-arch-dark" aria-hidden="true" />
      <svg viewBox="0 0 490 290" className="relative w-full h-auto" role="img" aria-label="Animated technology network: cloud, phones, connectivity, software, collaboration and AI nodes">
        {edges.map(([a, b], i) => (
          <line key={i} x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y}
            stroke="#2BD4C1" strokeWidth="1.3" strokeOpacity="0.55" strokeDasharray="4 9" className="animate-dashFlow"
            style={{ animationDelay: `${i * 0.3}s` }} />
        ))}
        {nodes.map((n, i) => (
          <g key={n.label} style={{ animation: `floatY ${5 + (i % 3)}s ease-in-out infinite`, animationDelay: `${i * 0.45}s` }}>
            <circle cx={n.x} cy={n.y} r={n.label === "AI & Automation" ? 34 : 28} fill="#152845" stroke="#15B8A6" strokeOpacity="0.65" strokeWidth="1.3" />
            <circle cx={n.x} cy={n.y} r="8" fill="#2BD4C1" opacity={0.5 + (i % 3) * 0.25} />
            <text x={n.x} y={n.y + (n.label === "AI & Automation" ? 54 : 48)} textAnchor="middle" fontSize="11" fontWeight="600" fill="#8FB3D9" style={{ fontFamily: "Inter, sans-serif" }}>{n.label}</text>
          </g>
        ))}
      </svg>
      <p className="relative mt-2 text-center text-xs font-semibold text-ice/50">Connections illuminate as opportunities are mapped.</p>
    </div>
  );
}

export default function TechnologySavings() {
  return (
    <main data-testid="page-technology-savings">
      <Seo
        title="Technology Cost Savings"
        description="Review internet, phone systems, cloud, software and collaboration technology costs — with independent specialists who evaluate both spending and efficiency."
        path="/technology-savings"
      />

      <section className="relative overflow-hidden bg-offwhite pt-28 lg:pt-36 pb-16 md:pb-24">
        <div className="absolute inset-0 bg-arch-fine" aria-hidden="true" />
        <div className="absolute -top-24 left-[5%] w-[480px] h-[480px] rounded-full bg-royal/10 blur-[110px]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel>Technology Cost Savings</SectionLabel>
            <KineticLines
              as="h1"
              className="mt-5 font-display font-extrabold tracking-tightest text-navy text-4xl sm:text-5xl leading-[1.06]"
              lines={["Better Technology.", <>Smarter <span className="text-teal-dark">Spending.</span></>]}
            />
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-slate">
                Your business technology should help you operate more effectively — not quietly drain your budget.
                Explore opportunities involving internet connectivity, business phone systems, cloud services,
                software, collaboration tools, and other technology expenses.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <a href="#tech-review" data-testid="technology-hero-cta" className="group mt-8 inline-flex items-center gap-2 bg-navy text-white font-bold text-[15px] px-7 py-4 rounded-full transition-all duration-300 hover:bg-teal hover:text-navy shadow-lift">
                Request a Technology Review
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.2}><TechNetworkViz /></Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionLabel>The Stack, Examined</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl leading-[1.1]" lines={["Every layer reviewed for cost — and for common sense."]} />
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {AREAS.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.05} className="h-full">
                <div className="group h-full rounded-2xl border border-line bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lift hover:border-teal/50" data-testid={`technology-area-${i + 1}`}>
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-ice group-hover:bg-teal-soft transition-colors">
                    <a.icon className="w-5 h-5 text-royal group-hover:text-teal-dark transition-colors" />
                  </span>
                  <h3 className="mt-4 font-display font-extrabold tracking-tight text-navy text-lg">{a.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate">{a.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-10 rounded-2xl border border-teal/40 bg-teal-soft/60 p-6 flex items-start gap-4" data-testid="technology-no-cost-note">
              <span className="mt-0.5 flex items-center justify-center w-9 h-9 rounded-full bg-teal text-navy font-extrabold shrink-0">✓</span>
              <p className="text-sm md:text-[15px] leading-relaxed text-navy">
                <strong>Where applicable, an initial technology evaluation is available at no cost.</strong> Any work
                beyond that is explained clearly — including any costs — before you decide anything.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ice/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <SectionLabel>Who This Helps</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl leading-[1.1]" lines={["If tech touches it, it's worth a look."]} />
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-slate">
                Offices that outgrew their phone system. Teams paying for software they barely open. Restaurants
                juggling connectivity across locations. Manufacturers where downtime costs more than the fix. If
                technology shows up on your monthly statement, it belongs in the review.
              </p>
            </Reveal>
          </div>
          <div>
            <SectionLabel>How The Process Works</SectionLabel>
            <div className="mt-6 space-y-6">
              {[
                ["Map your stack", "A quick conversation about the tools and services you rely on."],
                ["A specialist evaluates", "An independent specialist reviews costs, contracts, and efficiency opportunities."],
                ["Decide with clarity", "Options, potential savings, and costs explained — the decision stays yours."],
              ].map(([t, c], i) => (
                <Reveal key={t} delay={i * 0.08}>
                  <div className="flex gap-4">
                    <span className="flex items-center justify-center w-9 h-9 rounded-full bg-navy text-teal-bright text-xs font-extrabold shrink-0">{i + 1}</span>
                    <div>
                      <h3 className="font-display font-extrabold text-navy">{t}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate">{c}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FAQ items={FAQS} title="Technology Review Questions" label="FAQ" />

      <section id="tech-review" className="py-16 md:py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel>Request A Review</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl leading-[1.1]" lines={["Find out what your stack should really cost."]} />
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-slate max-w-lg">
                Tell us a little about your setup and we'll help you connect with a technology specialist. Where
                applicable, the first evaluation carries no cost.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}><LeadForm service="technology" heading="Request a Technology Review" /></Reveal>
        </div>
      </section>

      <ServiceCTA
        service="technology"
        title={["Technology should work for your budget", "— not against it."]}
        copy="Start with a quick conversation. We'll help you connect with a specialist who reviews the full stack."
        cta="Request a Technology Review"
      />
    </main>
  );
}
